import api from './api';

/**
 * Map API event → Frontend event
 */
function mapEvent(e) {
  return {
    id: e.id,
    title: e.title,
    description: e.description,
    location: e.location,
    startsAt: e.starts_at,
    endsAt: e.ends_at,
    allDay: e.all_day,
    color: e.color,
    status: e.status,
    createdBy: e.created_by?.name || '',
    createdAt: e.created_at,
    updatedAt: e.updated_at,
  };
}

/**
 * Map Frontend event → API event
 */
function mapEventToApi(data) {
  const mapped = {};

  if (data.title !== undefined) mapped.title = data.title;
  if (data.description !== undefined) mapped.description = data.description || null;
  if (data.location !== undefined) mapped.location = data.location || null;
  if (data.startsAt !== undefined) mapped.starts_at = data.startsAt;
  if (data.endsAt !== undefined) mapped.ends_at = data.endsAt || null;
  if (data.allDay !== undefined) mapped.all_day = data.allDay;
  if (data.color !== undefined) mapped.color = data.color || null;
  if (data.status !== undefined) mapped.status = data.status;

  return mapped;
}

/**
 * Map Frontend filters → API query params
 */
function mapFilters(params = {}) {
  const mapped = {};

  if (params.page) mapped.page = params.page;
  if (params.pageSize) mapped.per_page = params.pageSize;
  if (params.dateFrom) mapped.date_from = params.dateFrom;
  if (params.dateTo) mapped.date_to = params.dateTo;
  if (params.search) mapped.search = params.search;
  if (params.status) mapped.status = params.status;

  return mapped;
}

/**
 * Get published events for the public calendar (no auth required)
 * @param {Object} params - Query parameters
 * @returns {Promise} { data, total, page, pageSize }
 */
export const getEvents = async (params = {}) => {
  try {
    const response = await api.get('/events', { params: mapFilters(params) });
    const { data, meta } = response.data;

    return {
      data: data.map(mapEvent),
      total: meta?.total || data.length,
      page: meta?.current_page || 1,
      pageSize: meta?.per_page || 100,
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar eventos.');
  }
};

/**
 * Get a single published event (no auth required)
 * @param {number} id - Event ID
 * @returns {Promise} Event
 */
export const getEventById = async (id) => {
  try {
    const response = await api.get(`/events/${id}`);
    return mapEvent(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar evento.');
  }
};

/**
 * Get all events including drafts (admin only)
 * @param {Object} params - Query parameters
 * @returns {Promise} { data, total, page, pageSize }
 */
export const getAdminEvents = async (params = {}) => {
  try {
    const response = await api.get('/admin/events', { params: mapFilters(params) });
    const { data, meta } = response.data;

    return {
      data: data.map(mapEvent),
      total: meta?.total || data.length,
      page: meta?.current_page || 1,
      pageSize: meta?.per_page || 15,
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar eventos.');
  }
};

/**
 * Get a single event including drafts (admin only)
 * @param {number} id - Event ID
 * @returns {Promise} Event
 */
export const getAdminEventById = async (id) => {
  try {
    const response = await api.get(`/admin/events/${id}`);
    return mapEvent(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar evento.');
  }
};

/**
 * Create a new event (admin only)
 * @param {Object} data - Event data
 * @returns {Promise} Created event
 */
export const createEvent = async (data) => {
  try {
    const response = await api.post('/events', mapEventToApi(data));
    return mapEvent(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao criar evento.');
  }
};

/**
 * Update an event (admin only)
 * @param {number} id - Event ID
 * @param {Object} data - Event data
 * @returns {Promise} Updated event
 */
export const updateEvent = async (id, data) => {
  try {
    const response = await api.put(`/events/${id}`, mapEventToApi(data));
    return mapEvent(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao atualizar evento.');
  }
};

/**
 * Delete an event (admin only)
 * @param {number} id - Event ID
 * @returns {Promise} void
 */
export const deleteEvent = async (id) => {
  try {
    await api.delete(`/events/${id}`);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao remover evento.');
  }
};
